import { formatPercent, formatTrend } from '../../lib/formatters';
import type { DashboardRateResponse, RateStatus } from '../../types/extranet-api.types';

interface KpiCardProps {
  label: string;
  data: DashboardRateResponse | undefined;
  isLoading: boolean;
}

const STATUS_BAR_BG: Record<RateStatus, string> = {
  success: 'bg-extranet-success',
  warning: 'bg-extranet-warning',
  danger: 'bg-extranet-danger',
  unknown: 'bg-extranet-border-strong',
};

const TREND_TEXT = (delta: number) => {
  if (delta > 0) return 'text-extranet-success';
  if (delta < 0) return 'text-extranet-danger';
  return 'text-extranet-text-tertiary';
};

const TREND_ARROW = (delta: number) => {
  if (delta > 0) return '↑';
  if (delta < 0) return '↓';
  return '→';
};

export const KpiCard = ({ label, data, isLoading }: KpiCardProps) => {
  const status: RateStatus = data?.status ?? 'unknown';

  return (
    <article className="relative overflow-hidden rounded-lg border border-extranet-border bg-extranet-card p-5 flex flex-col gap-3">
      <div className={`absolute left-0 top-0 h-full w-[2px] ${STATUS_BAR_BG[status]}`} aria-hidden />

      <p className="text-xs font-medium text-extranet-text-secondary uppercase tracking-wide">{label}</p>

      {isLoading || !data ? (
        <div className="h-9 w-24 bg-extranet-card-muted rounded animate-pulse" aria-busy="true" />
      ) : (
        <p className="font-mono text-3xl font-semibold text-extranet-text-primary tabular-nums">
          {formatPercent(data.value)}
        </p>
      )}

      {isLoading || !data ? (
        <div className="h-4 w-32 bg-extranet-card-muted rounded animate-pulse" aria-busy="true" />
      ) : data.trend !== undefined ? (
        <p className={`text-xs ${TREND_TEXT(data.trend)} flex items-center gap-1`}>
          <span aria-hidden>{TREND_ARROW(data.trend)}</span>
          <span className="font-mono tabular-nums">{formatTrend(data.trend)}</span>
          <span className="text-extranet-text-tertiary">vs période précédente</span>
        </p>
      ) : (
        <p className="text-xs text-extranet-text-tertiary">— trend indisponible</p>
      )}
    </article>
  );
};
